export interface CloudProvider {
  id: string;
  name: string;
}

export interface CreateCloudQuota {
  instance_type?: string;
  gpu_type?: string;
  limit?: number;
}

export interface CloudPolicyRule {
  action?: string;
  conditions?: { [key: string]: any };
  description?: string;
}

export interface PolicyContext {
  name?: string;
  description?: string;
  tags?: string[];
}

export interface CreateCloudPolicyRequest {
  cloud_cred_id?: string;
  organization_id?: string;
  default_rule?: "DEFAULT_RULE_ALLOW" | "DEFAULT_RULE_DENY";
  quotas?: CreateCloudQuota[];
  context?: PolicyContext;
}

export interface UpdateCloudPolicyDefaultRuleRequest {
  id?: string;
  default_rule?: string;
}

export interface AddQuotasToCloudPolicyRequest {
  id?: string;
  quotas?: CreateCloudQuota[];
}

export interface UpdateCloudQuotaRequest {
  quota_id?: string;
  instance_type?: string;
  gpu_type?: string;
  limit?: number;
}

export interface ListCloudPolicyOptions {
  organization_id?: string;
  cloud_cred_id?: string;
  include_quota_current?: boolean;
}

export interface ListCloudPolicyRequest {
  options?: ListCloudPolicyOptions;
}

export interface PolicyContext {
  name?: string;
  description?: string;
  tags?: string[];
}

export interface CloudQuota {
  id?: string;
  instance_type?: string;
  gpu_type?: string;
  limit?: number;
}

export interface CloudPolicy {
  id?: string;
  created_at?: string; // timestamp
  updated_at?: string; // timestamp
  cloud_cred_id?: string;
  organization_id?: string;
  default_rule?: DefaultRule;
  quotas?: CloudQuota[];
  context?: PolicyContext;
}

export enum DefaultRule {
  DEFAULT_RULE_UNSPECIFIED = 0,
  DEFAULT_RULE_ALLOW = 1,
  DEFAULT_RULE_DENY = 2,
}

// Utility functions to handle DefaultRule enum/string conversion
export const getDefaultRuleString = (rule?: DefaultRule | number): string => {
  switch (rule) {
    case DefaultRule.DEFAULT_RULE_ALLOW:
    case 1:
      return "DEFAULT_RULE_ALLOW";
    case DefaultRule.DEFAULT_RULE_DENY:
    case 2:
      return "DEFAULT_RULE_DENY";
    default:
      return "DEFAULT_RULE_ALLOW"; // Default fallback
  }
};

export const getDefaultRuleEnum = (rule?: string): DefaultRule => {
  switch (rule) {
    case "DEFAULT_RULE_ALLOW":
      return DefaultRule.DEFAULT_RULE_ALLOW;
    case "DEFAULT_RULE_DENY":
      return DefaultRule.DEFAULT_RULE_DENY;
    default:
      return DefaultRule.DEFAULT_RULE_ALLOW; // Default fallback
  }
};

export const getDefaultRuleLabel = (rule?: DefaultRule | number): string => {
  switch (rule) {
    case DefaultRule.DEFAULT_RULE_ALLOW:
    case 1:
      return "Allow";
    case DefaultRule.DEFAULT_RULE_DENY:
    case 2:
      return "Deny";
    default:
      return "Allow"; // Default fallback
  }
};

export interface CloudQuotaUtilization {
  quota_id?: string;
  current?: number;
}

export interface PageParams {
  page_size?: number;
  page_token?: string;
}

export interface OrderOptions {
  order_by?: string;
  ordering?: string;
}

export interface ListCloudPolicyOptions {
  order_options?: OrderOptions;
  cloud_cred_id?: string;
  organization_id?: string;
  include_quota_current?: boolean;
}

// Response types for each route

// GET /org/:id/cloud-policies
export interface ListCloudPoliciesResponse {
  items?: CloudPolicy[];
  nextPageToken?: string;
}

// GET /org/:id/cloud-policies/:policyId
export interface GetCloudPolicyResponse {
  cloud_policy?: CloudPolicy;
}

// DELETE /org/:id/cloud-policies/:policyId
export interface DeleteCloudPolicyResponse {
  // Empty response
}

// PUT /org/:id/cloud-policies/:policyId
export interface UpdateCloudPolicyDefaultRuleResponse {
  cloud_policy?: CloudPolicy;
}

// GET /org/:id/cloud-policies/:policyId/utilization
export interface GetCloudPolicyUtilizationResponse {
  quota_utilizations?: CloudQuotaUtilization[];
}

// POST /org/:id/cloud-policies/:policyId/quotas
export interface AddQuotasToCloudPolicyResponse {
  cloud_policy?: CloudPolicy;
}

// PUT /org/:id/cloud-policies/:policyId/quotas/:quotaId
export interface UpdateCloudQuotaResponse {
  quota?: CloudQuota;
}

// DELETE /org/:id/cloud-policies/:policyId/quotas/:quotaId
export interface DeleteCloudQuotaResponse {
  // Empty response
}

// GET /org/:id/cloud-policies/:policyId/quotas/:quotaId/utilization
export interface GetCloudQuotaUtilizationResponse {
  quota_utilization?: CloudQuotaUtilization;
}
